import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import PropTypes from 'prop-types';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

const StatusDropdown = ({
  isOpen,
  onClose,
  selectedStatus = '',
  onStatusChange,
  buttonRef
}) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Use smart positioning hook
  const { dropdownRef, position } = useDropdownPosition(isOpen, 256, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a filter button - if so, don't close
      const isFilterButton = event.target.closest('[data-filter-button]');
      if (isFilterButton) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);

  const statusOptions = [
    {
      value: '',
      label: 'All Cases',
      description: 'Show both open and closed cases'
    },
    {
      value: 'Open',
      label: 'Open Cases',
      description: 'Cases that are currently active'
    },
    {
      value: 'Closed',
      label: 'Closed Cases',
      description: 'Cases that have been resolved'
    }
  ];

  // Handle status selection
  const handleStatusSelect = (status) => {
    onStatusChange(status);
    onClose();
  };

  // Handle clear
  const handleClear = () => {
    onStatusChange('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-64 sm:w-64'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)', // Ensure dropdown never exceeds viewport width
                minWidth: '280px' // Ensure minimum usable width
              }
        }
      >
        <div className="p-1.5 sm:p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-1.5 sm:mb-3">
            <h3 className="text-xs sm:text-sm font-medium text-gray-900">
              Case Status
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Selected Status */}
          {selectedStatus && (
            <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <div className="text-xs font-medium text-blue-700 mb-1">
                Selected:
              </div>
              <div className="text-sm text-blue-900">
                {statusOptions.find((opt) => opt.value === selectedStatus)
                  ?.label || selectedStatus}
              </div>
            </div>
          )}

          {/* Status Options */}
          <div className="space-y-2">
            {statusOptions.map((option, index) => {
              const isSelected = option.value === selectedStatus;
              const isFocused = focusedIndex === index;
              return (
                <button
                  key={option.value}
                  onClick={() => handleStatusSelect(option.value)}
                  className={`w-full p-2 sm:p-4 text-left border rounded-lg transition-colors ${
                    isSelected
                      ? 'bg-blue-50 border-blue-200 text-blue-900'
                      : isFocused
                        ? 'bg-gray-50 border-gray-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{option.label}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {option.description}
                      </div>
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-4">
            <button
              onClick={handleClear}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={!selectedStatus}
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

StatusDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedStatus: PropTypes.string,
  onStatusChange: PropTypes.func.isRequired,
  buttonRef: PropTypes.object
};

export default StatusDropdown;
